import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/**
 * Interfaz para los items de Región.
 * Según el endpoint /api/v1/header/obtenerRegiones/{id_perfil}/jerarquia/{id_jerarquia}
 */
export interface RegionItem {
  id_jerarquia: number;
  nombre: string;
}

/**
 * Interfaz para los items de Área (nivel jerárquico superior).
 * Según el endpoint /api/v1/header/obtenerAreas/{id_perfil}/jerarquia/{id_jerarquia}
 */
export interface AreaItem {
  id_jerarquia: number;
  descripcion: string;
}

/**
 * Interfaz para los items de Unidad Operativa (nivel jerárquico bajo el área).
 * Según el endpoint /api/v1/header/obtenerUnidadesOperativas/{id_perfil}/jerarquia/{id_jerarquia}
 */
export interface UnidadOperativaItem {
  id_jerarquia: number;
  descripcion: string;
}

@Injectable({
  providedIn: 'root'
})
export class HeaderService {
  private apiUrl = environment.apiUrl;
  private readonly http = inject(HttpClient);

  /**
   * Varias vistas (shell, dashboard, tarjetas) piden el mismo par perfil/área al montarse;
   * comparte una sola petición HTTP concurrente y libera la clave al terminar.
   */
  private unidadesOperativasEnCurso = new Map<string, Observable<UnidadOperativaItem[]>>();

  /**
   * Obtiene las Áreas del usuario desde la API según el perfil y jerarquía padre.
   * Endpoint: GET /api/v1/header/obtenerAreas/{id_perfil}/jerarquia/{id_jerarquia}
   * @param id_perfil ID del perfil del usuario
   * @param id_jerarquia ID de la jerarquía padre; -1 = sin dependencia a la izquierda
   * @returns Observable con el listado de Áreas (cada item tiene id_jerarquia a nivel área)
   */
  obtenerAreas(id_perfil: number, id_jerarquia: number): Observable<AreaItem[]> {
    return this.http.get<AreaItem[]>(
      `${this.apiUrl}/header/obtenerAreas/${id_perfil}/jerarquia/${id_jerarquia}`
    ).pipe(
      catchError(error => {
        console.error('Error al obtener áreas:', error);
        return of([]);
      })
    );
  }

  /**
   * Obtiene las regiones del usuario según el perfil.
   * Endpoint: GET /api/v1/header/obtenerRegiones/{id_perfil}/jerarquia/{id_jerarquia}
   */
  obtenerRegiones(id_perfil: number, id_jerarquia: number): Observable<RegionItem[]> {
    return this.http.get<RegionItem[]>(
      `${this.apiUrl}/header/obtenerRegiones/${id_perfil}/jerarquia/${id_jerarquia}`
    ).pipe(
      catchError(error => {
        console.error('Error al obtener regiones:', error);
        return of([]);
      })
    );
  }

  /**
   * Obtiene las Unidades Operativas del usuario desde la API según perfil y área.
   * Endpoint: GET /api/v1/header/obtenerUnidadesOperativas/{id_perfil}/jerarquia/{id_jerarquia}
   * @param id_perfil ID del perfil del usuario
   * @param id_jerarquia ID del área seleccionada; -1 = todas las áreas accesibles
   * @returns Observable con el listado de Unidades Operativas (cada item tiene id_jerarquia a nivel UO)
   */
  obtenerUnidadesOperativas(id_perfil: number, id_jerarquia: number): Observable<UnidadOperativaItem[]> {
    const key = `${id_perfil}:${id_jerarquia}`;
    let compartida = this.unidadesOperativasEnCurso.get(key);

    if (!compartida) {
      const url = `${this.apiUrl}/header/obtenerUnidadesOperativas/${id_perfil}/jerarquia/${id_jerarquia}`;
      compartida = this.http.get<UnidadOperativaItem[]>(url).pipe(
        catchError(error => {
          console.error('Error al obtener unidades operativas:', error);
          return of([]);
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
        finalize(() => this.unidadesOperativasEnCurso.delete(key))
      );
      this.unidadesOperativasEnCurso.set(key, compartida);
    }

    return compartida;
  }
}
